/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { BFScreen } from "@/components/standard/screen/BFScreen"
import { LoanAddressHeader } from "@/screens/loans/components/LoanAddressHeader"
import { useLayoutEffect } from "react"
import {
  $preQual,
  clearPreQualState,
  convertPreQualToLoanProfile,
  initPreQualFromProfile,
  type PreQualState,
  useFinishedPreQual,
} from "@/stores/preQual"
import sdk from "@/api/sdk"
import { fbqTrackCustom, fbqUserAttributes } from "@/utils/PixelUtils"
import { queryClient } from "@/queries/client"
import { Sentry } from "@/hooks/sentry"
import { useUpsertLoanPreQual } from "@/queries/loan"
import { useUser } from "@/queries/user"
import BFText, { H2, Subtitle } from "@/components/standard/BFText"
import { ScrollView, View } from "react-native"
import { MultiQuestionScreen } from "@/screens/prequal/MultiQuestionScreen"
import {
  PrimaryButton,
  SecondaryButton,
} from "@/components/buttons/CommonButtons"
import { router } from "expo-router"
import { Vertical } from "@/components/standard/BFLayout"
import { useSafeAreaInsets } from "react-native-safe-area-context"

const QUESTIONS: Array<keyof PreQualState> = [
  "propertiesFlipped",
  "creditScore",
  "cashAvailable",
  "intentStatus",
]

export default function Qualification() {
  const upsertPreQualMutation = useUpsertLoanPreQual()
  const isDone = useFinishedPreQual(QUESTIONS)
  const { data: user } = useUser()
  const insets = useSafeAreaInsets()

  useLayoutEffect(() => {
    initPreQualFromProfile(user?.me?.loanProfile || undefined, true)
  }, [user])

  function saveAndLeave() {
    const preQualState = $preQual.get()
    const flips = convertPreQualToLoanProfile().propertiesFlipped
    void sdk.updateUserProfileFlips({ flips })
    const fbqAttributes = fbqUserAttributes(preQualState, user)

    fbqTrackCustom("CompletedPreQualFlow", fbqAttributes)

    upsertPreQualMutation.mutate(
      {
        input: {
          ...convertPreQualToLoanProfile(),
        },
      },
      {
        onSuccess: () => {
          clearPreQualState()
          void queryClient.invalidateQueries({ queryKey: ["user"] })
          router.replace("/loans/products")
        },
        onError: (e) => {
          console.error(e)
          Sentry.captureException(e)
          router.replace("/")
        },
      }
    )
  }

  function skipAndLeave() {
    clearPreQualState()
    router.replace("/")
  }

  return (
    <BFScreen
      header={<LoanAddressHeader className={"py-3"} />}
      headerSticky={true}
      noBackButton={true}
    >
      <Vertical
        style={{ paddingBottom: insets.bottom, paddingTop: insets.top }}
        className={"rounded-t-3xl bg-off-white flex-1 max-w-[550px] mx-auto"}
      >
        <H2 color={"primary"} className={"p-5 mt-3"}>
          Let&apos;s get you <BFText color={"primaryLight"}>funded</BFText>
        </H2>
        <Subtitle color={"primary"} className={"px-5"}>
          Answer a few short questions so we can create the best quote for your
          next deal from Backflip.
        </Subtitle>
        <ScrollView className="flex-1 p-5">
          <MultiQuestionScreen questions={QUESTIONS} />
        </ScrollView>
        <View
          className={
            "bg-white p-5 justify-between w-full gap-3 flex flex-row border-t border-light-grey"
          }
        >
          <SecondaryButton
            title={"Skip"}
            testID={"s"}
            className={"flex-1"}
            onPress={skipAndLeave}
          />
          <PrimaryButton
            onPress={saveAndLeave}
            disabled={!isDone}
            className={"flex-[2]"}
            size={"lg"}
            title={"Next: See Loan Options"}
            testID={"next-loan-options"}
          />
        </View>
      </Vertical>
    </BFScreen>
  )
}
